/** @jsxImportSource theme-ui */
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Flex, Text } from "theme-ui";
import { colors, typography } from "../../styles/theme";
import { Icon } from "../atoms";

const normalizeOption = (opt) =>
  typeof opt === "string" ? { value: opt, label: opt } : opt;

const FilterBar = ({
  filters,
  onClearAll,
  showClearAll = true,
  onFilterSelectionChange,
}) => {
  const [openFilterId, setOpenFilterId] = useState(null);
  const barRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) {
        setOpenFilterId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleFilter = (filter) => {
    setOpenFilterId((prev) => (prev === filter.id ? null : filter.id));
    filter.onClick?.(filter);
  };

  const handleOptionToggle = (filter, optionValue) => {
    const selected = filter.selectedValues || [];
    const next = selected.includes(optionValue)
      ? selected.filter((v) => v !== optionValue)
      : [...selected, optionValue];
    onFilterSelectionChange?.(filter.id, next);
  };

  return (
    <Box ref={barRef} as="section" className="filter-bar" sx={filterBarStyles}>
      <Flex sx={filterBarFlexStyles}>
        {filters?.length > 0 && filters.map((filter) => {
          const isOpen = openFilterId === filter.id;
          const options = (filter.options || []).map(normalizeOption);
          return (
            <Box
              key={filter.id}
              sx={{
                position: "relative",
                display: ["block", "block", "inline-block", "inline-block"],
                width: ["100%", "100%", "auto", "auto"],
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                className="filter-bar-pill"
                onClick={() => toggleFilter(filter)}
                sx={filterBarPillStyles}
              >
                <Icon
                  name={filter.iconName}
                  color={colors.primaryMain}
                  size={20}
                  customStyles={{ flexShrink: 0 }}
                />
                <Text
                  sx={{
                    ...typography.textMd500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {filter.label}
                  {typeof filter.selectedCount === "number" &&
                    filter.selectedCount > 0 && (
                      <Text as="span" sx={{ ml: "0.125rem" }}>
                        ({filter.selectedCount})
                      </Text>
                    )}
                </Text>
                <Icon
                  name={isOpen ? "ChevronUp" : "ChevronDown"}
                  color={colors.primaryMain}
                  size={24}
                  customStyles={{ marginLeft: "auto", flexShrink: 0 }}
                />
              </Box>
              {isOpen && options.length > 0 && (
                <Box
                  className="filter-bar-dropdown"
                  sx={filterBarDropdownStyles}
                >
                  {options.map((opt) => {
                    const selected = (filter.selectedValues || []).includes(
                      opt.value,
                    );
                    return (
                      <Box
                        key={opt.value}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOptionToggle(filter, opt.value);
                        }}
                        sx={filterBarDropdownOptionStyles}
                      >
                        <Box
                          sx={{
                            ...filterBarOptionsCheckboxStyles,
                            border: `1px solid ${
                              selected ? colors.primaryMain : colors.neutral400
                            }`,
                            backgroundColor: selected
                              ? colors.primaryMain
                              : "transparent",
                          }}
                        >
                          {selected && (
                            <Icon
                              name="Check"
                              color={colors.white}
                              size={24}
                              customStyles={{ strokeWidth: 3 }}
                            />
                          )}
                        </Box>
                        <Text
                          sx={{
                            ...typography.textMd400,
                            whiteSpace: "nowrap",
                            minWidth: 0,
                          }}
                        >
                          {opt.label}
                        </Text>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
        {showClearAll && (
          <Text
            as="button"
            type="button"
            className="filter-bar-clear-all"
            onClick={onClearAll}
            sx={filterBarClearAllStyles}
          >
            Clear All
          </Text>
        )}
      </Flex>
    </Box>
  );
};

FilterBar.propTypes = {
  /** Array of filter configs. Each can come from DatoCMS and include id, label, iconName, selectedCount, selectedValues, options, onClick */
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      iconName: PropTypes.string.isRequired,
      selectedCount: PropTypes.number,
      /** Currently selected option values (used for dropdown checkboxes) */
      selectedValues: PropTypes.arrayOf(PropTypes.string),
      onClick: PropTypes.func,
      /** Option values for dropdown: [{ value, label }] or string[] */
      options: PropTypes.array,
    }),
  ).isRequired,
  /** Called when "Clear All" is clicked */
  onClearAll: PropTypes.func,
  showClearAll: PropTypes.bool,
  /** Called when selection changes: (filterId, selectedValues) */
  onFilterSelectionChange: PropTypes.func,
};

FilterBar.defaultProps = {
  onClearAll: () => {},
  showClearAll: true,
  onFilterSelectionChange: () => {},
};

const {
  filterBarStyles,
  filterBarFlexStyles,
  filterBarPillStyles,
  filterBarDropdownStyles,
  filterBarDropdownOptionStyles,
  filterBarOptionsCheckboxStyles,
  filterBarClearAllStyles,
} = {
  filterBarStyles: {
    position: "sticky",
    top: 80,
    zIndex: 100,
    width: "100%",
    bg: colors.primary1000,
    py: "2rem",
    px: ["1rem", "1rem", "2rem", "2rem"],
  },
  filterBarFlexStyles: {
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: ["left", "left", "left", "center"],
    gap: ["1.5rem", "1.5rem", "1rem", "1.5rem"],
    maxWidth: "105rem",
    margin: "0 auto",
  },
  filterBarPillStyles: {
    display: ["flex", "flex", "inline-flex", "inline-flex"],
    width: ["100%", "100%", "auto", "auto"],
    alignItems: "center",
    gap: "0.5rem",
    px: "0.875rem",
    py: "0.625rem",
    backgroundColor: colors.white,
    borderRadius: "0.625rem",
    cursor: "pointer",
    border: `1px solid ${colors.neutral300}`,
    minHeight: "2.75rem",
    boxSizing: "border-box",
    "&:hover": {
      backgroundColor: colors.neutral200,
    },
  },
  filterBarDropdownStyles: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: "0.25rem",
    minWidth: "max(100%, 16rem)",
    width: "max-content",
    maxWidth: "min(20rem, 95vw)",
    backgroundColor: colors.white,
    border: `1px solid ${colors.neutral200}`,
    borderRadius: "0.5rem",
    zIndex: 100,
    py: "0.01rem",
    maxHeight: "20rem",
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: `${colors.neutral300} transparent`,
  },
  filterBarDropdownOptionStyles: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    px: "1.25rem",
    py: "0.625rem",
    cursor: "pointer",
    borderBottom: `1px solid ${colors.neutral200}`,
    "&:first-of-type": {
      borderTopLeftRadius: "0.5rem",
      borderTopRightRadius: "0.5rem",
    },
    "&:last-of-type": {
      borderBottomLeftRadius: "0.5rem",
      borderBottomRightRadius: "0.5rem",
      borderBottom: "none",
    },
    "&:hover": {
      backgroundColor: colors.primary100,
    },
  },
  filterBarOptionsCheckboxStyles: {
    width: "1.125rem",
    height: "1.125rem",
    borderRadius: "0.188rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  filterBarClearAllStyles: {
    ...typography.h6,
    color: colors.white,
    background: "none",
    border: "none",
    cursor: "pointer",
    outline: "none",
    "&:hover": {
      opacity: 0.9,
    },
    "&:focus": {
      outline: "none",
    },
  },
};

export default FilterBar;